import { DownloadOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@nks/shared';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Tabs, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { downloadCsv } from './csv';
import { createFollowUp, exportRetentionCsv, listRetention, type RetentionList, type RetentionRow } from './api';

const { Text } = Typography;

/**
 * Bikes that have gone quiet or are old enough to upgrade. Each row can become
 * a follow-up call, but only for a customer who agreed to marketing messages:
 * win-back and upgrade calls are marketing, and the server refuses them otherwise.
 */
export function RetentionPage(): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canFollowUp = useHasPermission(PERMISSIONS.FOLLOWUP_MANAGE);
  const [list, setList] = useState<RetentionList>('no-service');
  const [creating, setCreating] = useState<string | null>(null);

  const { data, isFetching, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['retention', list],
    queryFn: ({ pageParam }) => (pageParam !== undefined ? listRetention(list, pageParam) : listRetention(list)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });
  const rows = data?.pages.flatMap((p) => p.items) ?? [];

  const create = useMutation({
    mutationFn: (row: RetentionRow) =>
      createFollowUp({
        type: list === 'upgrade' ? 'UPGRADE' : 'REACTIVATION',
        customerUid: row.customerUid,
        unitUid: row.unitUid,
      }),
    onMutate: (row) => { setCreating(row.unitUid); },
    onSettled: () => { setCreating(null); },
    onSuccess: (_created, row) => {
      void message.success(t('serviceDesk.retention.created', { name: row.customerName }));
      void queryClient.invalidateQueries({ queryKey: ['retention'] });
      void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('serviceDesk.retention.createFailed'));
    },
  });

  const bike = (r: RetentionRow): string => (r.plateNo !== null ? `${r.model} · ${r.plateNo}` : r.model);
  const sinceCol = list === 'upgrade' ? t('serviceDesk.retention.col.delivered') : t('serviceDesk.retention.col.lastService');
  const since = (r: RetentionRow): string =>
    list === 'upgrade'
      ? formatDate(r.deliveredAt)
      : r.lastServiceAt === undefined
        ? t('serviceDesk.retention.neverServiced')
        : formatDate(r.lastServiceAt);

  const actions = (row: RetentionRow): ReactNode => (
    <Flex vertical gap={6} align="flex-start">
      <Flex gap={8} wrap>
        {row.hasOpenFollowUp ? (
          <Tag color="processing">{t('serviceDesk.retention.callWaiting')}</Tag>
        ) : (
          canFollowUp && (
            <Button
              type="primary"
              size="large"
              loading={creating === row.unitUid}
              // A win-back or upgrade call is marketing; the reason is shown under the button.
              disabled={!row.marketingConsent}
              title={row.marketingConsent ? undefined : t('serviceDesk.retention.noConsent')}
              onClick={(e) => {
                e.stopPropagation();
                create.mutate(row);
              }}
            >
              {t('serviceDesk.retention.createFollowUp')}
            </Button>
          )
        )}
      </Flex>
      {row.marketingConsent ? (
        <Tag color="success">{t('serviceDesk.retention.consentAgreed')}</Tag>
      ) : (
        <Flex vertical gap={2} align="flex-start">
          <Tag>{t('serviceDesk.retention.consentNotAgreed')}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('serviceDesk.retention.noConsent')}
          </Text>
        </Flex>
      )}
    </Flex>
  );

  return (
    <Flex vertical gap={12}>
      <Tabs
        activeKey={list}
        onChange={(key) => { setList(key as RetentionList); }}
        items={[
          { key: 'no-service', label: t('serviceDesk.retention.tabs.noService') },
          { key: 'upgrade', label: t('serviceDesk.retention.tabs.upgrade') },
        ]}
      />
      <Text type="secondary">
        {list === 'upgrade' ? t('serviceDesk.retention.help.upgrade') : t('serviceDesk.retention.help.noService')}
      </Text>
      {error !== null && (
        <Text type="danger">{error instanceof ApiError ? error.message : t('serviceDesk.retention.loadFailed')}</Text>
      )}
      <ListPage<RetentionRow>
        title={t('serviceDesk.retention.title')}
        loading={isFetching}
        emptyDescription={list === 'upgrade' ? t('serviceDesk.retention.emptyUpgrade') : t('serviceDesk.retention.emptyNoService')}
        actions={
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              void downloadCsv(t, () => exportRetentionCsv(list), `retention-${list}.csv`, 'serviceDesk.retention.exportFailed')
            }
          >
            {t('serviceDesk.retention.exportCsv')}
          </Button>
        }
        columns={[
          { title: t('serviceDesk.retention.col.customer'), key: 'customer', render: (_: unknown, r) => r.customerName },
          { title: t('serviceDesk.retention.col.phone'), key: 'phone', width: 150, render: (_: unknown, r) => r.phone },
          { title: t('serviceDesk.retention.col.bike'), key: 'bike', render: (_: unknown, r) => bike(r) },
          { title: sinceCol, key: 'since', width: 130, render: (_: unknown, r) => since(r) },
          { title: t('serviceDesk.retention.col.action'), key: 'action', width: 360, render: (_: unknown, r) => actions(r) },
        ]}
        rows={rows}
        rowKey={(row) => row.unitUid}
        hasMore={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        renderMobileRow={(row) => (
          <Flex vertical gap={6}>
            <Text strong>{row.customerName}</Text>
            <Text>{bike(row)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {sinceCol}: {since(row)} · {row.phone}
            </Text>
            {actions(row)}
          </Flex>
        )}
      />
    </Flex>
  );
}
